import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Formik } from 'formik';
import { RootState } from 'store';

import { AuthTags } from '~constants/authTags';

import { getChangedValues } from '~libs/dataUtils';
import generateYupSchema from '~libs/yupValidation';

import { Button } from '~components';
import ContainerHeader from '~components/ContainerHeader';
import ContainerLoading from '~components/ContainerLoading';
import { FieldProps, Fields } from '~components/form-fields';
import Icon, { faArrowLeft } from '~components/icon';
import Modal from '~components/Modal';
import SendDetailsModal from '~care-workers/components/send-user-details-modal/SendDetailsModal';

import editFields from '~care-workers/forms/userEdit';

import { getOne, update } from '~care-workers/actions';
import { getAll as getAllowances } from '~care-workers/actions/userAllowances';
import { getAll as getUserDepartments } from '~care-workers/actions/userDepartments';
import { getAll as getUserPayGroups } from '~care-workers/actions/userPayGroups';
import { getAll as getUserPayLevels } from '~care-workers/actions/userPayLevels';
import { getAll as getStates } from '~lookups/actions/states';
import { getAll as getUserLanguages } from '~lookups/actions/userLanguages';
import { getAll as getCompanyWorksites } from '~lookups/actions/workSites';
import { setTitle } from '~main/actions/login';

import { clearUser } from '~care-workers/reducers/users';
import { clearAddressFields } from '~lookups/reducers/addressLookup';

import { selectCurrentUser, selectUsersLoading } from '~care-workers/selectors/users';
import { selectAddressFields } from '~lookups/selectors/addressLookup';

const User: React.FC = () => {
  const [invalidFields, setInvalidFields]: [string[], any] = useState([]);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const [showSendDetailsModal, setShowSendDetailsModal] = useState(false);
  // Link record from selector to component
  const user = useAppSelector(selectCurrentUser);
  const userLoading = useAppSelector(selectUsersLoading);

  const fieldDefinitions = Object.entries(editFields).map(([key, props]) => ({
    key,
    ...props,
  }));

  const attributes = fieldDefinitions.filter((field) => field.isAttribute !== false).map((field) => field.key);

  const associations = fieldDefinitions.map((field) => field.association).filter(Boolean);

  useEffect(() => {
    setTitle(`Edit User`);
    // Get user by ID on mount
    dispatch(getOne({ id, attributes, associations }));

    // Get selector values on mount
    dispatch(getUserDepartments());
    dispatch(getUserPayGroups());
    dispatch(getUserPayLevels());
    dispatch(getStates());
    dispatch(getAllowances());
    dispatch(getCompanyWorksites());
    dispatch(getUserLanguages());

    // Clean component on unmount
    return () => {
      dispatch(clearAddressFields());
      dispatch(clearUser());
    };
  }, []);

  useEffect(() => {
    setTitle(`Edit ${user.full_name}`);
  }, [user]);

  const initVals =
    user ||
    Object.keys(editFields).reduce(
      (acc, key) => {
        const { field } = editFields[key];
        if (field) {
          acc[field] = '';
        }
        return acc;
      },
      {} as Record<string, any>,
    );

  const onBack = () => {
    navigate('/users');
  };

  const onSubmit = async (values: any) => {
    const { payload } = await dispatch(update({ id, ...values }));
    if (payload?.success) {
      navigate('/users');
    }
  };

  const AddressUpdater = (setFieldValue: any) => {
    const states = useAppSelector(({ lookups }: RootState) => lookups.states);
    const { address, suburb, postcode, state } = useAppSelector(selectAddressFields);

    useEffect(() => {
      if (address) setFieldValue('address', address);
      if (suburb) setFieldValue('suburb', suburb);
      if (postcode) setFieldValue('postcode', postcode);
      if (state) {
        const stateId = states.rows.find((record) => record.name === state)?.id;
        if (stateId) setFieldValue('state_id', stateId);
      }
    }, [address, suburb, postcode, state, setFieldValue]);

    return null;
  };

  const isLoading = userLoading === 'pending';

  const fieldsObject: FieldProps = fieldDefinitions.reduce((acc, field) => {
    const { key, ...fieldProps } = field;
    acc[key] = fieldProps;
    return acc;
  }, {} as FieldProps);

  const validationSchema = generateYupSchema(fieldsObject);

  const handleYupValidation = async (values: any) => {
    try {
      await validationSchema.validate(values, { abortEarly: false });
      setInvalidFields([]);
    } catch (err: any) {
      if (err.inner) {
        const errors = err.inner.map((e: any) => e.path);
        console.error(errors);
        setInvalidFields(errors);
      }
    }
  };

  return (
    <div className="p-2">
      {!isLoading && (
        <>
          <ContainerHeader className="warning">
            <span>User Details</span>
          </ContainerHeader>
          <div className="nav d-flex justify-content-between bg-light p-2 mt-2 mb-2">
            <div>
              <Button size="sm" className="me-2" onClick={() => onBack()}>
                <Icon className="me-2" icon={faArrowLeft} />
                Back to User Listing
              </Button>
              <Button size="sm" className="me-2" disabled={true}>
                Care Worker Summary
              </Button>
              <Button size="sm" className="me-2" disabled={true}>
                Show Password Field
              </Button>
              <Button
                size="sm"
                className="me-2"
                authTag={AuthTags.USERS.ADMIN.UPDATE}
                onClick={() => setShowSendDetailsModal(true)}
              >
                Send Login Details
              </Button>
            </div>
          </div>
          <ContainerLoading loading={isLoading} bgColor="white" color="black" isTransparent={false}>
            <Formik
              enableReinitialize
              initialValues={initVals}
              validationSchema={validationSchema}
              validate={handleYupValidation}
              validateOnMount={false}
              validateOnChange={false}
              validateOnBlur={false}
              onSubmit={(values, { setSubmitting }) => {
                onSubmit(getChangedValues(values, initVals));
                setSubmitting(false);
              }}
            >
              {({ values, setFieldValue, setFieldTouched, touched, isSubmitting }) => (
                <Form>
                  <Fields
                    definitions={fieldDefinitions}
                    row={user}
                    invalidFields={invalidFields}
                    values={values}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    touched={touched}
                  />
                  <AddressUpdater setFieldValue={setFieldValue} />
                  <div>
                    <Button
                      size="sm"
                      disabled={isSubmitting}
                      className="ms-4 me-4 ps-4 pe-4"
                      color="success"
                      type="submit"
                    >
                      Update User
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
            <Modal
              setIsOpen={setShowSendDetailsModal}
              isOpen={showSendDetailsModal}
              header={'Send Login Details To This User'}
              scrollable
              size="xl"
              centered
            >
              <SendDetailsModal email={user.email} mobile={user.mobile} id={id ?? ''} />
            </Modal>
          </ContainerLoading>
        </>
      )}
    </div>
  );
};

export default User;
